import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useLocalePath, useT } from "@/i18n/LocaleContext";

/* Footer — mirrors Quantum US centred architecture, adapted for the AIFM entity.
   IntersectionObserver-driven staged reveal (wordmark → tagline → gold rule → links → address → group line → copyright). */
export const Footer = () => {
  const { t } = useT();
  const localePath = useLocalePath();
  const year = new Date().getFullYear();
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || !("IntersectionObserver" in window)) { setVisible(true); return; }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { setVisible(true); io.unobserve(e.target); } });
    }, { threshold: 0.15 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const links = [
    { to: localePath("/legal#legal-notice"), label: t("footer.links.legal_notice"), testid: "footer-legal-notice" },
    { to: localePath("/legal#privacy"), label: t("footer.links.privacy"), testid: "footer-privacy" },
    { to: localePath("/legal#cookies"), label: t("footer.links.cookies"), testid: "footer-cookies" },
    { to: localePath("/contact"), label: t("footer.links.contact"), testid: "footer-contact" },
  ];

  return (
    <footer
      data-testid="site-footer"
      className={["bg-navy text-cream mt-24 md:mt-40 footer-stage", visible ? "is-visible" : ""].join(" ")}
      ref={ref}
    >
      <div className="max-w-[1080px] mx-auto px-6 sm:px-10 lg:px-12 py-24 sm:py-28 lg:py-32 text-center">
        <p
          className="stage-item font-serif text-cream font-normal"
          style={{ fontSize: "clamp(56px, 7vw, 112px)", lineHeight: "1", letterSpacing: "-0.01em" }}
        >
          {t("common.entity_short", "QUANTUM")}
        </p>
        <p
          className="stage-item mt-10 sm:mt-12 font-serif italic text-cream/85"
          style={{ fontSize: "clamp(18px, 1.9vw, 26px)" }}
        >
          {t("footer.tagline")}
        </p>

        <span aria-hidden="true" className="stage-rule mt-20 sm:mt-24 block h-px w-12 bg-gold mx-auto" />

        <ul
          className="stage-item mt-10 sm:mt-12 flex flex-wrap items-center justify-center gap-x-6 gap-y-3 font-sans uppercase text-cream/60"
          style={{ fontSize: "11px", letterSpacing: "0.22em" }}
        >
          {links.map((l, i) => (
            <li key={l.testid} className="inline-flex items-center gap-6">
              <Link
                to={l.to}
                data-testid={l.testid}
                className="group inline-flex hover:text-cream transition-colors duration-500"
              >
                <span className="relative">
                  {l.label}
                  <span
                    aria-hidden
                    className="absolute left-0 right-0 -bottom-1 h-px bg-gold scale-x-0 origin-left group-hover:scale-x-100 transition-transform duration-[600ms] ease-out"
                  />
                </span>
              </Link>
              {i < links.length - 1 && (
                <span aria-hidden className="inline-block bg-gold" style={{ width: "4px", height: "4px" }} />
              )}
            </li>
          ))}
        </ul>

        <div className="stage-item mt-14 sm:mt-16">
          <address
            data-testid="footer-address"
            className="not-italic font-sans text-[12.5px] text-cream/60 leading-relaxed"
          >
            <span>Quantum Capital Management S.A.</span>
            <span aria-hidden className="mx-2 text-cream/30">·</span>
            <span>{t("common.city")}</span>
            <span aria-hidden className="mx-2 text-cream/30">·</span>
            <a
              href="mailto:office@qcmlux.com"
              data-testid="footer-email-link"
              className="hover:text-cream transition-colors duration-500"
            >
              office@qcmlux.com
            </a>
            <span aria-hidden className="mx-2 text-cream/30">·</span>
            <a
              href="tel:+35220420122"
              data-testid="footer-telephone-link"
              className="hover:text-cream transition-colors duration-500"
            >
              +352 20 42 01 22
            </a>
          </address>
        </div>

        <p
          data-testid="footer-group-line"
          className="stage-item mt-16 font-sans uppercase text-cream/45"
          style={{ fontSize: "10.5px", letterSpacing: "0.28em" }}
        >
          {t("footer.group_line")}
        </p>

        <p
          data-testid="footer-copyright"
          className="stage-item mt-6 font-sans text-cream/45"
          style={{ fontSize: "11px" }}
        >
          {t("footer.copyright").replace("{year}", String(year))}
        </p>
      </div>
    </footer>
  );
};

export default Footer;
